/**
 * ScrollSpy Hook - Virtual Pointer Navigation
 *
 * A Phoenix LiveView hook that highlights the current section in a navigation
 * menu as the user scrolls through content. Uses a "virtual pointer" model
 * for precise control, especially when sections at the bottom of the page
 * can't scroll to the top of the viewport.
 *
 * ## How It Works
 *
 * The hook tracks a "virtual pointer" position that moves down the page:
 *
 * 1. **Normal scrolling**: The pointer sits at a fixed offset from the viewport
 *    top (below the navbar and section nav). As the user scrolls, both the
 *    pointer and viewport move together.
 *
 * 2. **Trouble zone**: When the page can't scroll any further (bottom sections),
 *    the viewport stops but the pointer continues moving down. This allows
 *    all sections to be selectable even if they can't reach the trigger line.
 *
 * 3. **Section detection**: The section "under" the virtual pointer is highlighted.
 *
 * ## Usage
 *
 * Add the hook to a nav element containing section links:
 *
 * ```heex
 * <nav id="section-nav" phx-hook="ScrollSpy" class="sticky top-14">
 *   <ul>
 *     <li><a href="#section-intro">Intro</a></li>
 *     <li><a href="#section-features">Features</a></li>
 *     <li><a href="#section-pricing">Pricing</a></li>
 *   </ul>
 * </nav>
 * ```
 *
 * The corresponding sections should have matching IDs:
 *
 * ```heex
 * <section id="section-intro">...</section>
 * <section id="section-features">...</section>
 * <section id="section-pricing">...</section>
 * ```
 *
 * ## Required Structure
 *
 * - Nav must contain `<li>` elements (receives `.active` class)
 * - Each `<li>` must contain an `<a href="#section-id">` link
 * - Section elements must have IDs matching the href (without #)
 * - Main navbar should have class `.app-navbar` for offset calculation
 *
 * ## Styling
 *
 * Style the active state with CSS:
 *
 * ```css
 * #section-nav li.active {
 *   background: var(--highlight-color);
 * }
 * ```
 *
 * ## Configuration
 *
 * Set `this.debug = true` in mounted() to show a red line indicating
 * the virtual pointer position (useful for debugging).
 *
 * @module hooks/scroll_spy
 */

export const ScrollSpy = {
  // Get element's true document position (not relative to offset parent)
  getDocumentTop(el) {
    return el.getBoundingClientRect().top + window.scrollY;
  },

  mounted() {
    this.items = Array.from(this.el.querySelectorAll("li"));
    this.links = Array.from(this.el.querySelectorAll('a[href^="#"]'));
    this.sections = [];
    this.isScrolling = false;
    this.debug = false; // Set to true to show virtual pointer line

    // Build sections array
    this.links.forEach((link, i) => {
      const id = link.getAttribute("href").slice(1);
      const section = document.getElementById(id);
      if (section)
        this.sections.push({ el: section, link, item: this.items[i] });
    });

    // Calculate nav heights
    const mainNav = document.querySelector(".app-navbar");
    this.mainNavHeight = mainNav ? mainNav.offsetHeight : 56;
    this.sectionNavHeight = this.el.offsetHeight;
    this.offset = this.mainNavHeight + this.sectionNavHeight + 20;

    // Create debug line if enabled
    if (this.debug) {
      this.debugLine = document.createElement("div");
      this.debugLine.style.cssText =
        "position:absolute;left:0;right:0;height:2px;background:red;z-index:9999;pointer-events:none;";
      document.body.appendChild(this.debugLine);
    }

    // Calculate document metrics
    this.recalculate();

    // Initialize virtual pointer at current scroll position
    this.virtualPointer = this.offset + window.scrollY;

    // Recalculate on resize
    this.onResize = () => this.recalculate();
    window.addEventListener("resize", this.onResize, { passive: true });

    // Wheel handler - moves the virtual pointer and scrolls page to follow
    this.onWheel = (e) => {
      e.preventDefault(); // Always prevent native scroll to avoid interference

      if (this.isScrolling) return;
      this.wheelScrolling = true; // Flag to ignore our own scroll event

      // Move pointer by wheel delta
      this.virtualPointer += e.deltaY;

      // Clamp pointer to valid range
      this.virtualPointer = Math.max(
        this.contentTop,
        Math.min(this.contentBottom, this.virtualPointer),
      );

      // Scroll page to match pointer (clamped to valid scroll range)
      const targetScroll = this.virtualPointer - this.offset;
      const clampedScroll = Math.max(0, Math.min(this.maxScroll, targetScroll));
      window.scrollTo({ top: clampedScroll, behavior: "instant" });

      this.updateActive();

      // Clear flag after scroll event has fired
      requestAnimationFrame(() => {
        this.wheelScrolling = false;
      });
    };
    window.addEventListener("wheel", this.onWheel, { passive: false });

    // Scroll handler - for non-wheel scroll sources (scrollbar, keyboard)
    // Syncs pointer to scroll position so they stay aligned
    this.onScroll = () => {
      if (this.isScrolling || this.wheelScrolling) return;

      const normalRangeEnd = this.offset + this.maxScroll;

      // Only sync pointer if it's currently in normal range (not in trouble zone)
      // This prevents late scroll events from resetting pointer when user is
      // navigating the trouble zone at the bottom
      if (this.virtualPointer <= normalRangeEnd) {
        const pointerFromScroll = this.offset + window.scrollY;
        this.virtualPointer = Math.max(
          this.contentTop,
          Math.min(normalRangeEnd, pointerFromScroll),
        );
      }

      this.updateActive();
    };
    window.addEventListener("scroll", this.onScroll, { passive: true });

    // Click handler
    this.el.addEventListener("click", (e) => {
      const link = e.target.closest('a[href^="#"]');
      if (!link) return;
      e.preventDefault();

      const sectionIndex = this.sections.findIndex((s) => s.link === link);
      if (sectionIndex === -1) return;
      const section = this.sections[sectionIndex];

      // Clear any pending timeout from previous click
      if (this.scrollTimeout) clearTimeout(this.scrollTimeout);

      // Set active immediately and remember target
      this.setActive(section);
      this.clickTarget = section;

      // Move pointer to section top (use true document position)
      this.virtualPointer = this.getDocumentTop(section.el);

      // Update debug line immediately
      if (this.debug && this.debugLine) {
        this.debugLine.style.top = this.virtualPointer + "px";
      }

      // Show navbar when clicking nav link (better UX)
      const mainNav = document.querySelector(".app-navbar");
      const sectionNav = document.querySelector(".app-section-nav");
      if (mainNav) mainNav.classList.remove("navbar-hidden");
      if (sectionNav) sectionNav.classList.remove("navbar-hidden");

      // Scroll to section, pausing scroll updates
      this.isScrolling = true;
      const sectionTop = this.getDocumentTop(section.el);
      const top = Math.min(sectionTop - this.offset, this.maxScroll);
      window.scrollTo({ top, behavior: "smooth" });

      // Resume scroll updates after animation, re-assert target
      this.scrollTimeout = setTimeout(() => {
        this.isScrolling = false;
        if (this.clickTarget) this.setActive(this.clickTarget);
      }, 1000);
    });

    // Initial state
    this.updateActive();
  },

  destroyed() {
    window.removeEventListener("wheel", this.onWheel);
    window.removeEventListener("scroll", this.onScroll);
    window.removeEventListener("resize", this.onResize);
    if (this.debugLine) this.debugLine.remove();
  },

  recalculate() {
    this.maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    this.contentTop = this.offset;
    this.contentBottom = document.documentElement.scrollHeight;
  },

  updateActive() {
    // If we have a click target, keep it active until user scrolls away
    if (this.clickTarget) {
      const targetTop = this.getDocumentTop(this.clickTarget.el) - this.offset;
      const scrollDiff = Math.abs(window.scrollY - targetTop);
      if (scrollDiff > 100) {
        this.clickTarget = null;
      } else {
        return;
      }
    }

    // Update debug line
    if (this.debug && this.debugLine) {
      this.debugLine.style.top = this.virtualPointer + "px";
    }

    // Find the section the pointer is in
    let active = this.sections[0];
    for (let i = 0; i < this.sections.length; i++) {
      const section = this.sections[i];
      const sectionTop = this.getDocumentTop(section.el);
      const isLast = i === this.sections.length - 1;
      const nextTop = isLast
        ? this.contentBottom
        : this.getDocumentTop(this.sections[i + 1].el);

      // Use <= for last section to include the boundary
      if (
        this.virtualPointer >= sectionTop &&
        (isLast
          ? this.virtualPointer <= nextTop
          : this.virtualPointer < nextTop)
      ) {
        active = section;
        break;
      }
    }

    this.setActive(active);
  },

  setActive(section) {
    // Skip if already active
    if (section.item.classList.contains("active")) return;

    // Remove active from all
    this.items.forEach((item) => item.classList.remove("active"));
    // Add active to current
    section.item.classList.add("active");
    // Scroll nav to show active
    section.item.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  },
};
